import GuideArticle from '../components/GuideArticle'
import type { RelatedCalc, HowToStep } from '../components/GuideArticle'
import type { QA } from '../components/Faq'
import { useLocale, localizedHref } from '../i18n/locale'

interface Content {
  title: string
  h1: string
  description: string
  category: string
  faqs: QA[]
  related: RelatedCalc[]
  // Step-by-step data for HowTo schema. Mirrors the visible <ol> in the body
  // but as plain text so Google's rich result can render the step cards.
  steps: HowToStep[]
}

const STEPS_EN: HowToStep[] = [
  { name: 'Design and measure', text: 'Finalise the layout, cabinets, counters and appliances on paper before anything is demolished.' },
  { name: 'Demolition', text: 'Remove old cabinets, counters, appliances and flooring as needed.' },
  { name: 'Rough-in', text: 'Plumbing, gas and electrical changes happen now, while walls are open, with inspections if required.' },
  { name: 'Drywall and paint prep', text: 'Patch and prime walls; doing most painting before cabinets go in is easier.' },
  { name: 'Flooring', text: 'Install continuous floors like tile or hardwood now; floating floors often go in after cabinets — check the manufacturer\'s guidance.' },
  { name: 'Cabinets', text: 'Install and level the boxes — everything above depends on these being right.' },
  { name: 'Countertops', text: 'Templated to the installed cabinets, then fabricated and installed (the built-in wait of one to three weeks).' },
  { name: 'Backsplash', text: 'Tile the backsplash once counters are in.' },
  { name: 'Appliances and fixtures', text: 'Hook up the sink, faucet, dishwasher, range and hood.' },
  { name: 'Finishing', text: 'Final paint touch-ups, cabinet hardware, trim and lighting.' },
]

const STEPS_FR: HowToStep[] = [
  { name: 'Concevoir et mesurer', text: 'Finalisez la disposition, les armoires, les comptoirs et les électroménagers sur papier avant toute démolition.' },
  { name: 'Démolition', text: 'Retirez les vieilles armoires, comptoirs, électroménagers et revêtements de sol au besoin.' },
  { name: 'Installation brute', text: 'Les changements de plomberie, de gaz et d\'électricité se font maintenant, pendant que les murs sont ouverts, avec inspections si requises.' },
  { name: 'Gypse et préparation à la peinture', text: 'Réparez et apprêtez les murs; faire l\'essentiel de la peinture avant la pose des armoires est plus facile.' },
  { name: 'Plancher', text: 'Posez maintenant les planchers continus (carrelage, bois franc); les planchers flottants se posent souvent après les armoires — vérifiez les directives du fabricant.' },
  { name: 'Armoires', text: 'Installez et mettez de niveau les caissons — tout ce qui suit dépend de leur bonne pose.' },
  { name: 'Comptoirs', text: 'Gabariés selon les armoires installées, puis fabriqués et posés (l\'attente incontournable de une à trois semaines).' },
  { name: 'Dosseret', text: 'Carrelez le dosseret une fois les comptoirs en place.' },
  { name: 'Électroménagers et appareils', text: 'Raccordez l\'évier, le robinet, le lave-vaisselle, la cuisinière et la hotte.' },
  { name: 'Finition', text: 'Retouches finales de peinture, quincaillerie d\'armoires, moulures et éclairage.' },
]

const EN: Content = {
  title: 'Kitchen Remodel Planning Guide: Order of Work & Checklist | RenoSheets',
  h1: 'Kitchen Remodel Planning Guide',
  description:
    'How to plan a kitchen remodel: scope and layout, the correct trade-by-trade order of work, where flooring and cabinets fit, and the materials to line up before you start.',
  category: 'Planning',
  related: [
    { label: 'Tile Calculator', href: '/calculators/tile/', blurb: 'Backsplash and floor tile plus thinset, grout and spacers.' },
    { label: 'Flooring Calculator', href: '/calculators/flooring/', blurb: 'Boxes of laminate, vinyl or hardwood with underlayment.' },
    { label: 'Paint Calculator', href: '/calculators/paint/', blurb: 'Walls, trim and ceiling paint with all the supplies.' },
  ],
  steps: STEPS_EN,
  faqs: [
    {
      q: 'What is the right order for a kitchen remodel?',
      a: 'Design and demolition come first, then plumbing and electrical rough-in, then drywall and paint prep, then flooring, then cabinets, then countertops (templated to the installed cabinets), then the backsplash, then appliances and plumbing fixtures, and finally trim and hardware. Cabinets go in before countertops because counters are measured to the actual installed cabinets.',
    },
    {
      q: 'How long does a kitchen remodel take?',
      a: 'A full kitchen remodel commonly takes six to twelve weeks from demolition to finish. Countertops add a built-in pause — most stone and quartz tops are templated after cabinets are installed and then fabricated off-site, which typically adds one to three weeks on its own.',
    },
    {
      q: 'Should I install flooring before or after cabinets?',
      a: 'It depends on the flooring. Continuous floors like tile or hardwood are often run before cabinets so the floor is unbroken and appliances can be moved later. Floating floors (click-lock laminate/vinyl) are usually installed after cabinets so the cabinet weight does not pin the floating floor and restrict its movement. Follow the flooring manufacturer’s instructions.',
    },
    {
      q: 'What part of a kitchen remodel should I prioritise?',
      a: 'Spend first on the things that are hard to change later: the layout, the cabinets, and the plumbing/electrical behind the walls. Finishes like the backsplash, hardware, and paint are comparatively easy and inexpensive to update down the road, so they are good places to economise if the budget is tight.',
    },
    {
      q: 'Can I remodel a kitchen myself?',
      a: 'Many steps — demo, painting, backsplash tile, hardware, and even cabinet assembly — are realistic DIY jobs. Plumbing, gas, and electrical work should be done by licensed pros (and often must be by code), and countertop fabrication usually requires a specialist. A common approach is to DIY the finishes and hire out the trades.',
    },
  ],
}

const FR: Content = {
  title: 'Guide de planification pour rénover une cuisine : ordre des travaux et liste | RenoSheets',
  h1: 'Guide de planification pour rénover une cuisine',
  description:
    'Comment planifier la rénovation d’une cuisine : portée et disposition, le bon ordre des travaux métier par métier, où s’insèrent le plancher et les armoires, et les matériaux à prévoir avant de commencer.',
  category: 'Planification',
  related: [
    { label: 'Calculateur de carrelage', href: '/calculators/tile/', blurb: 'Carrelage de dosseret et de plancher, plus mortier-colle, coulis et croisillons.' },
    { label: 'Calculateur de plancher', href: '/calculators/flooring/', blurb: 'Boîtes de stratifié, de vinyle ou de bois franc avec sous-couche.' },
    { label: 'Calculateur de peinture', href: '/calculators/paint/', blurb: 'Peinture pour murs, moulures et plafond, avec toutes les fournitures.' },
  ],
  steps: STEPS_FR,
  faqs: [
    {
      q: 'Quel est le bon ordre pour rénover une cuisine?',
      a: 'La conception et la démolition viennent d’abord, puis l’installation brute de la plomberie et de l’électricité, puis le gypse et la préparation à la peinture, puis le plancher, puis les armoires, puis les comptoirs (gabariés selon les armoires installées), puis le dosseret, puis les électroménagers et appareils de plomberie, et enfin les moulures et la quincaillerie. Les armoires se posent avant les comptoirs parce que les comptoirs sont mesurés selon les armoires réellement installées.',
    },
    {
      q: 'Combien de temps prend la rénovation d’une cuisine?',
      a: 'Une rénovation complète de cuisine prend couramment de six à douze semaines, de la démolition à la finition. Les comptoirs ajoutent une pause incontournable — la plupart des comptoirs en pierre et en quartz sont gabariés une fois les armoires installées, puis fabriqués en atelier, ce qui ajoute généralement de une à trois semaines à lui seul.',
    },
    {
      q: 'Faut-il poser le plancher avant ou après les armoires?',
      a: 'Cela dépend du revêtement. Les planchers continus comme le carrelage ou le bois franc sont souvent posés avant les armoires pour que le plancher soit sans interruption et que les électroménagers puissent être déplacés plus tard. Les planchers flottants (stratifié ou vinyle à clic) sont habituellement posés après les armoires, pour que le poids des armoires ne coince pas le plancher flottant et ne restreigne pas son mouvement. Suivez les instructions du fabricant du revêtement.',
    },
    {
      q: 'Quelle partie de la rénovation d’une cuisine faut-il prioriser?',
      a: 'Investissez d’abord dans ce qui est difficile à changer plus tard : la disposition, les armoires et la plomberie ou l’électricité derrière les murs. Les finitions comme le dosseret, la quincaillerie et la peinture sont relativement faciles et peu coûteuses à mettre à jour par la suite; ce sont donc de bons endroits où économiser si le budget est serré.',
    },
    {
      q: 'Puis-je rénover une cuisine moi-même?',
      a: 'Beaucoup d’étapes — démolition, peinture, carrelage du dosseret, quincaillerie et même l’assemblage des armoires — sont des travaux réalistes à faire soi-même. La plomberie, le gaz et l’électricité devraient être confiés à des pros licenciés (et le code l’exige souvent), et la fabrication des comptoirs requiert habituellement un spécialiste. Une approche courante consiste à faire les finitions soi-même et à confier les corps de métier à des pros.',
    },
  ],
}

export default function GuideKitchenRemodel() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)

  return (
    <GuideArticle
      title={c.title}
      h1={c.h1}
      description={c.description}
      path="/guides/kitchen-remodel-planning-guide/"
      updated="2026-06-04"
      category={c.category}
      faqs={c.faqs}
      related={c.related}
      howToSteps={c.steps}
    >
      {locale === 'fr' ? (
        <>
          <p>
            Une cuisine est la pièce la plus complexe que la plupart des propriétaires rénoveront un jour — plusieurs corps de
            métier, de longs délais de livraison des matériaux, et un ordre des opérations strict où un seul faux pas (comme
            commander les comptoirs avant que les armoires soient posées) peut coûter des semaines. Ce guide explique comment
            planifier le projet et la séquence que les travaux devraient suivre.
          </p>

          <h2>Planifier la portée et la disposition</h2>
          <ul>
            <li>Décidez si vous rafraîchissez (peinture, quincaillerie, dosseret) ou si vous changez complètement la disposition. Déplacer la plomberie, le gaz ou les murs est un projet bien plus important qui exige habituellement des permis.</li>
            <li>Gardez le <strong>triangle de travail</strong> (évier, cuisinière, réfrigérateur) efficace lorsque vous planifiez une nouvelle disposition.</li>
            <li>Commandez tôt les articles à long délai — les armoires et les comptoirs peuvent prendre des semaines, et les électroménagers peuvent être en rupture de stock.</li>
            <li>Établissez un budget réaliste avec une réserve, et décidez quelles étapes vous ferez vous-même par rapport à celles que vous confierez à des pros.</li>
          </ul>

          <h2>Ordre des travaux</h2>
          <ol>
            <li><strong>Concevoir et mesurer.</strong> Finalisez la disposition, les armoires, les comptoirs et les électroménagers sur papier avant toute démolition.</li>
            <li><strong>Démolition.</strong> Retirez les vieilles armoires, comptoirs, électroménagers et revêtements de sol au besoin.</li>
            <li><strong>Installation brute.</strong> Les changements de plomberie, de gaz et d’électricité se font maintenant, pendant que les murs sont ouverts — avec inspections si requises.</li>
            <li><strong>Gypse et préparation à la peinture.</strong> Réparez et apprêtez les murs; faire l’essentiel de la peinture avant la pose des armoires est plus facile.</li>
            <li><strong>Plancher.</strong> Posez maintenant les planchers continus (carrelage, bois franc); les planchers flottants se posent souvent après les armoires — vérifiez les directives du fabricant. Estimez avec le <a href={L('/calculators/flooring/')}>Calculateur de plancher</a> ou le <a href={L('/calculators/tile/')}>Calculateur de carrelage</a>.</li>
            <li><strong>Armoires.</strong> Installez et mettez de niveau les caissons — tout ce qui suit dépend de leur bonne pose.</li>
            <li><strong>Comptoirs.</strong> Gabariés selon les armoires installées, puis fabriqués et posés (l’attente incontournable).</li>
            <li><strong>Dosseret.</strong> Carrelez le dosseret une fois les comptoirs en place. Obtenez le carrelage, le mortier-colle et le coulis avec le <a href={L('/calculators/tile/')}>Calculateur de carrelage</a>.</li>
            <li><strong>Électroménagers et appareils.</strong> Raccordez l’évier, le robinet, le lave-vaisselle, la cuisinière et la hotte.</li>
            <li><strong>Finition.</strong> Retouches finales de peinture, quincaillerie d’armoires, moulures et éclairage.</li>
          </ol>

          <h2>Matériaux à prévoir</h2>
          <ul>
            <li><strong>Carrelage de dosseret et de plancher</strong> — plus le mortier-colle, le coulis et les croisillons (le <a href={L('/calculators/tile/')}>Calculateur de carrelage</a> les inclut).</li>
            <li><strong>Plancher</strong> — boîtes, plus sous-couche et transitions.</li>
            <li><strong>Peinture et apprêt</strong> — consultez <a href={L('/guides/how-to-paint-a-room/')}>comment peindre une pièce</a> pour la technique et le <a href={L('/calculators/paint/')}>Calculateur de peinture</a> pour les quantités.</li>
            <li>Armoires, comptoirs, électroménagers, évier, robinet, éclairage et quincaillerie.</li>
          </ul>

          <h2>Respectez l’échéancier</h2>
          <p>
            Avec autant de dépendances, un tableau vaut mieux qu’une simple liste de tâches. Utilisez le <a href={L('/kanban-board/')}>tableau Kanban</a> gratuit —
            le modèle « Rénovation de cuisine » préremplit ces tâches pour que vous suiviez chaque métier, de Planification à
            Soumission, En cours et Terminé.
          </p>
        </>
      ) : (
        <>
          <p>
            A kitchen is the most complex room most homeowners will ever renovate — multiple trades, long material lead times,
            and a strict order of operations where one misstep (like ordering countertops before cabinets are in) can cost
            weeks. This guide lays out how to plan the project and the sequence the work should follow.
          </p>

          <h2>Plan the scope and layout</h2>
          <ul>
            <li>Decide whether you're refreshing (paint, hardware, backsplash) or doing a full layout change. Moving plumbing, gas, or walls is a much bigger project and usually needs permits.</li>
            <li>Keep the <strong>work triangle</strong> (sink, stove, fridge) efficient when planning a new layout.</li>
            <li>Order long-lead items early — cabinets and countertops can take weeks, and appliances can be backordered.</li>
            <li>Set a realistic budget with contingency, and decide which steps you'll DIY versus hire out.</li>
          </ul>

          <h2>Order of work</h2>
          <ol>
            <li><strong>Design &amp; measure.</strong> Finalise the layout, cabinets, counters, and appliances on paper before anything is demolished.</li>
            <li><strong>Demolition.</strong> Remove old cabinets, counters, appliances, and flooring as needed.</li>
            <li><strong>Rough-in.</strong> Plumbing, gas, and electrical changes happen now, while walls are open — with inspections if required.</li>
            <li><strong>Drywall &amp; paint prep.</strong> Patch and prime walls; doing most painting before cabinets go in is easier.</li>
            <li><strong>Flooring.</strong> Install continuous floors (tile, hardwood) now; floating floors often go in after cabinets — check the manufacturer's guidance. Estimate with the <a href={L('/calculators/flooring/')}>Flooring Calculator</a> or <a href={L('/calculators/tile/')}>Tile Calculator</a>.</li>
            <li><strong>Cabinets.</strong> Install and level the boxes — everything above depends on these being right.</li>
            <li><strong>Countertops.</strong> Templated to the installed cabinets, then fabricated and installed (the built-in wait).</li>
            <li><strong>Backsplash.</strong> Tile the backsplash once counters are in. Get tile, thinset and grout from the <a href={L('/calculators/tile/')}>Tile Calculator</a>.</li>
            <li><strong>Appliances &amp; fixtures.</strong> Hook up the sink, faucet, dishwasher, range, and hood.</li>
            <li><strong>Finishing.</strong> Final paint touch-ups, cabinet hardware, trim, and lighting.</li>
          </ol>

          <h2>Materials to line up</h2>
          <ul>
            <li><strong>Backsplash &amp; floor tile</strong> — plus thinset, grout, and spacers (the <a href={L('/calculators/tile/')}>Tile Calculator</a> includes them).</li>
            <li><strong>Flooring</strong> — boxes plus underlayment and transitions.</li>
            <li><strong>Paint &amp; primer</strong> — see <a href={L('/guides/how-to-paint-a-room/')}>how to paint a room</a> for technique and the <a href={L('/calculators/paint/')}>Paint Calculator</a> for quantities.</li>
            <li>Cabinets, countertops, appliances, sink, faucet, lighting, and hardware.</li>
          </ul>

          <h2>Stay on schedule</h2>
          <p>
            With this many dependencies, a board beats a to-do list. Use the free <a href={L('/kanban-board/')}>kanban board</a> —
            the "Kitchen Renovation" template pre-fills these tasks so you can track each trade from Planning through Quoted,
            In Progress, and Done.
          </p>
        </>
      )}
    </GuideArticle>
  )
}
